import { BaseTextFieldProps, lighten, darken, useTheme } from '@mui/material';
import React from 'react';
import Select, { ControlProps, GroupBase, defaultTheme, Theme } from 'react-select';

import _ from 'lodash';

export type OptionType = { value: unknown; label: string };
export type OptionsType = Array<OptionType>;
export type FlexySelectProps = Omit<React.ComponentProps<typeof Select>, 'options'> & {
    error?: BaseTextFieldProps['error'];
    helperText?: BaseTextFieldProps['helperText'];
    size?: BaseTextFieldProps['size'];
    options: OptionsType | Promise<OptionsType>;
};
/**
 * FlexySelect permet de sélectionner une valeur pré-défini dans une liste déroulante.
 */
export const FlexySelect: React.FunctionComponent<FlexySelectProps> = function ({
    options,
    error,
    size,
    ...props
}: FlexySelectProps) {
    const [asyncOptions, setAsyncOptions] = React.useState<OptionsType>([]);

    if (Array.isArray(options)) {
        if (!_.isEqual(asyncOptions, options)) {
            setAsyncOptions(options);
        }
    } else {
        options.then(setAsyncOptions);
    }

    const theme = useTheme();

    const getBorderColor = () => {
        const errorColor = theme.palette.error?.main;
        const colorFromPalette = theme.palette.divider;
        return `${error ? errorColor : colorFromPalette}`;
    };

    const overidesStyle = function (state: ControlProps<unknown, boolean, GroupBase<unknown>>) {
        const base = {
            borderWidth: '1px',
            borderRadius: '12px',
            color: theme.palette.text?.primary || 'inherit',
            backgroundColor: theme.palette.background.paper,
            borderColor: getBorderColor(),
            ':hover': {
                borderColor: error ? theme.palette.error?.main : theme.palette.secondary.main,
            },
        };
        if (state.isFocused) {
            return {
                ...base,
                borderWidth: '2px',
            };
        }
        if (state.isDisabled) {
            return {
                ...base,
                color: theme.palette.text.secondary,
                opacity: '1',
                backgroundColor: theme.palette.action.disabledBackground,
            };
        }
        return base;
    };

    const getTheme = function (): Theme {
        const themised = {
            ...defaultTheme,
        };

        if (theme.palette.primary) {
            themised.colors = {
                ...defaultTheme.colors,
                primary: theme.palette.primary.main,
                primary75: theme.palette.primary.light,
                primary50: lighten(theme.palette.primary.main, 0.5),
                primary25: lighten(theme.palette.primary.main, 0.25),
            };
        }
        if (theme.palette.warning) {
            themised.colors.danger = theme.palette.warning.main;
            themised.colors.dangerLight = theme.palette.warning.light;
        }

        return themised;
    };

    return (
        <Select
            styles={{
                control: (baseStyles, state) => ({
                    ...baseStyles,
                    ...overidesStyle(state),
                }),
                menu: (base) => ({
                    ...base,
                    backgroundColor: theme.palette.background.paper || base.backgroundColor,
                }),

                singleValue: (base) => ({
                    ...base,
                    color: theme.palette.text.primary || base.color,
                }),

                input: (base) => ({
                    ...base,
                    padding: size === 'medium' ? '16.5px 14px' : undefined,
                    margin: '0px 0px 0px 0px',
                    borderRadius: '12px',
                    color: theme.palette.text.primary || base.color,
                }),

                placeholder: (base) => ({
                    ...base,
                    color: theme.palette.text?.primary,
                    opacity: '0.42',
                }),

                // Fix Z-index issue
                menuPortal: (base) => {
                    const newBase = {
                        ...base,
                        borderRadius: '12px',
                        zIndex: 9999,
                    };
                    return newBase;
                },

                menuList: (base) => ({
                    ...base,

                    '::-webkit-scrollbar': {
                        width: '8px',
                        height: '0px',
                    },
                    '::-webkit-scrollbar-track': {
                        background: darken(theme.palette.background.paper, 0.1),
                    },
                    '::-webkit-scrollbar-thumb': {
                        borderRadius: '12px',
                        background: theme.palette.secondary.main || base.background,
                    },
                    '::-webkit-scrollbar-thumb:hover': {
                        background: lighten(theme.palette.secondary.main, 0.3) || base.background,
                    },
                }),

                option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected
                        ? theme.palette.primary.main || base.backgroundColor
                        : theme.palette.background.paper,
                }),
            }}
            theme={getTheme()}
            {...props}
            options={asyncOptions}
            menuPosition="fixed"
        />
    );
};

// const FlexySelect = withTheme(FlexySelectRaw);
